import { App, Form, Input } from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomActionButtons,
  CustomInput,
  CustomModal,
  CustomSelect,
  Loader,
} from "sipli-fleet-core/components";
import {
  useMutationCreateRelayStatusType,
  useMutationUpdateRelayStatusType,
  useQueryGetRelayStatusTypeById,
} from "src/queries/administrator/relayStatusTypes.queries";
import type { RelayStatusTypeRecord } from "src/services/administrator/relayStatusTypesService";

interface RelayStatusTypesFormModalProps {
  open: boolean;
  record?: RelayStatusTypeRecord | null;
  onClose: () => void;
}

interface RelayStatusTypeFormValues {
  name: string;
  active: string;
  description?: string;
}

const getDetailRecord = (
  payload?: unknown,
): Partial<RelayStatusTypeRecord> => {
  const response = payload as
    | RelayStatusTypeRecord
    | { relayStatusType?: RelayStatusTypeRecord }
    | { data?: RelayStatusTypeRecord }
    | undefined;

  if (!response) return {};
  if ("relayStatusType" in response && response.relayStatusType) {
    return response.relayStatusType;
  }
  if ("data" in response && response.data) return response.data;
  return response as RelayStatusTypeRecord;
};

const isActive = (value: unknown) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    return ["active", "true", "1"].includes(value.toLowerCase());
  }
  return false;
};

const RelayStatusTypesFormModal = ({
  open,
  record,
  onClose,
}: RelayStatusTypesFormModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<RelayStatusTypeFormValues>();
  const isEditing = Boolean(record?.id);
  const editId = record?.id;

  const detailQuery = useQueryGetRelayStatusTypeById(editId, {
    enabled: open && isEditing,
  });
  const createMutation = useMutationCreateRelayStatusType();
  const updateMutation = useMutationUpdateRelayStatusType();
  const detailRecord = getDetailRecord(detailQuery.data?.data?.response);

  const statusOptions = useMemo(
    () => [
      { label: t("active"), value: "true" },
      { label: t("inactive"), value: "false" },
    ],
    [t],
  );

  useEffect(() => {
    if (!open) return;

    form.setFieldsValue({
      name: "",
      active: "true",
      description: "",
    });
  }, [form, open]);

  useEffect(() => {
    if (!open || !isEditing || !detailRecord.id) return;

    form.setFieldsValue({
      name: detailRecord.name ?? "",
      active: String(isActive(detailRecord.active)),
      description: detailRecord.description ?? "",
    });
  }, [detailRecord, form, isEditing, open]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const now = new Date().toISOString();

    try {
      if (isEditing && editId) {
        await updateMutation.mutateAsync({
          id: editId,
          payload: {
            id: editId,
            active: values.active === "true",
            description: values.description?.trim() ?? "",
            name: values.name.trim(),
            code: detailRecord.code ?? values.name.trim(),
            usedForMapping: Boolean(detailRecord.usedForMapping),
            baseType: Boolean(detailRecord.baseType),
            createDate: detailRecord.createDate,
            createdBy: detailRecord.createdBy ?? "sysadmin",
            updateDate: now,
            updatedBy: "sysadmin",
          },
        });
        message.success(t("relay_status_type_updated"));
      } else {
        await createMutation.mutateAsync({
          active: values.active === "true",
          description: values.description?.trim() ?? "",
          name: values.name.trim(),
          code: values.name.trim(),
          usedForMapping: false,
          baseType: false,
          createDate: now,
          createdBy: "sysadmin",
        });
        message.success(t("relay_status_type_created"));
      }

      handleClose();
    } catch {
      message.error(t("setup_save_failed"));
    }
  };

  return (
    <CustomModal
      open={open}
      title={t(
        isEditing
          ? "edit_relay_status_type"
          : "add_relay_status_type",
      )}
      onCancel={handleClose}
      destroyOnHidden
      width={560}
      footer={null}
    >
      {detailQuery.isLoading ? (
        <Loader height="h-[310px]" />
      ) : (
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          className="grid grid-cols-1 pt-4 gap-x-4 sm:grid-cols-2"
        >
          <Form.Item
            name="name"
            label={t("name")}
            rules={[
              { required: true, whitespace: true, message: t("name_required") },
              { max: 64, message: t("name_max_length", { count: 64 }) },
            ]}
            className="mb-3!"
          >
            <CustomInput placeholder={t("enter_name")} maxLength={64} />
          </Form.Item>

          <Form.Item
            name="active"
            label={t("status")}
            rules={[{ required: true, message: t("status_required") }]}
            className="mb-3!"
          >
            <CustomSelect options={statusOptions} placeholder={t("select")} />
          </Form.Item>

          <Form.Item
            name="description"
            label={t("description")}
            rules={[
              {
                max: 255,
                message: t("description_too_long"),
              },
            ]}
            className="sm:col-span-2 mb-3!"
          >
            <Input.TextArea
              rows={4}
              placeholder={t("enter_description")}
              maxLength={255}
              className="px-2 py-4 custom-input no-resize"
            />
          </Form.Item>

          <Form.Item className="sm:col-span-2 mb-0!">
            <CustomActionButtons
              onCancel={handleClose}
              onOk={handleSubmit}
              loading={createMutation.isPending || updateMutation.isPending}
              okProps={{ htmlType: "button" }}
              cancelBtnLabel="cancel"
              okBtnLabel={isEditing ? "update" : "add"}
            />
          </Form.Item>
        </Form>
      )}
    </CustomModal>
  );
};

export default RelayStatusTypesFormModal;
